import React, { useState, useEffect, useMemo } from 'react';
import {
  GitBranch, GitCommitVertical, ChevronDown, ChevronRight,
  Search, FileText, ListTree,
  CircleDot, GitPullRequest, ShieldCheck, ShieldX,
  Hash, User, Calendar, Box, Server, Folder,
  MessageSquare, Trash2, Plus, FilePlus2, ExternalLink,
  Tag, Lock, Archive, Heart, Smile, Award, ThumbsUp, ThumbsDown
} from 'lucide-react';

// ============================================================================
// Operation registry
// ============================================================================
const OPERATIONS = new Set([
  'create_or_update_file', 'push_files', 'get_file_contents',
  'create_repository', 'fork_repository', 'create_branch', 'search_repositories',
  'create_issue', 'create_pull_request', 'list_issues', 'update_issue',
  'add_issue_comment', 'get_issue', 'delete_repository',
  'search_code', 'search_issues', 'search_users',
  'list_commits'
]);

// ============================================================================
// Helpers
// ============================================================================
const shortSha = (s) => s ? s.slice(0, 7) : '-------';
const fmtNum = (n) => typeof n === 'number' ? n.toLocaleString() : n;

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYday = d.toDateString() === yesterday.toDateString();
  const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return `Today at ${t}`;
  if (isYday) return `Yesterday at ${t}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: today.getFullYear() === d.getFullYear() ? undefined : 'numeric' })} at ${t}`;
}

function statusGlyph(status) {
  switch (status) {
    case 'added': return { ch: 'A', cls: 'st-A', label: 'Added' };
    case 'modified': return { ch: 'M', cls: 'st-M', label: 'Modified' };
    case 'removed': case 'deleted': return { ch: 'D', cls: 'st-D', label: 'Deleted' };
    case 'renamed': return { ch: 'R', cls: 'st-R', label: 'Renamed' };
    case 'copied': return { ch: 'C', cls: 'st-C', label: 'Copied' };
    default: return { ch: '?', cls: 'st-U', label: status || 'Unknown' };
  }
}

function buildPath(operation, input) {
  const i = input || {};
  const { owner, repo } = i;
  if (/^search_/.test(operation)) {
    const q = i.query || i.q || '';
    return `search · ${operation.replace('search_', '')}${q ? ` · "${q}"` : ''}`;
  }
  let base = owner && repo ? `~/${owner}/${repo}` : '~/(no repo)';
  switch (operation) {
    case 'list_commits': return base + '/commits' + (i.sha ? ` @${i.sha}` : '');
    case 'list_issues': return base + '/issues';
    case 'create_issue': return base + '/issues/new';
    case 'get_issue':
    case 'update_issue':
    case 'add_issue_comment':
      return base + (i.issue_number ? `/issues/${i.issue_number}` : '/issues');
    case 'create_pull_request':
      return base + (i.head && i.base ? `/pulls (${i.head} → ${i.base})` : '/pulls');
    case 'create_branch':
      return base + (i.branch ? `/branches/${i.branch}` : '/branches');
    case 'create_or_update_file':
    case 'get_file_contents':
      return base + (i.path ? `/${i.path}` : '') + (i.branch ? ` @${i.branch}` : '');
    case 'push_files':
      return base + `/push${i.branch ? ` @${i.branch}` : ''}${i.files ? ` (${i.files.length} files)` : ''}`;
    case 'fork_repository': return `${base} → ${i.organization || 'your account'}`;
    case 'create_repository': return `~/(new) ${i.name || ''}`;
    case 'delete_repository': return `${base} (delete)`;
    default: return base;
  }
}

// External link affordance
const ExtLink = ({ href, children }) => href ? (
  <a className="dp-link" href={href} target="_blank" rel="noreferrer noopener">
    {children || 'View on GitHub'} <ExternalLink size={9} />
  </a>
) : null;

// Reactions summary (issue/PR/comment)
function ReactionsRow({ reactions }) {
  if (!reactions || !reactions.total_count) return null;
  const items = [
    { k: '+1', icon: ThumbsUp },
    { k: '-1', icon: ThumbsDown },
    { k: 'heart', icon: Heart },
    { k: 'hooray', icon: Award },
    { k: 'laugh', icon: Smile },
  ].filter(({ k }) => reactions[k] > 0);
  if (items.length === 0) return null;
  return (
    <span className="dp-reactions">
      {items.map(({ k, icon: Icon }) => (
        <span key={k} className="dp-rx"><Icon size={9} /> {reactions[k]}</span>
      ))}
    </span>
  );
}

// Raw response footer — collapsible
function RawFooter({ data, label = 'response' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="dp-raw-footer">
      <button className="dp-raw-toggle" onClick={() => setOpen(o => !o)}>
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        Show raw {label}
      </button>
      {open && (
        <pre className="dp-raw">{data === null || data === undefined ? '(empty body — 204 No Content)' : JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
}

// ============================================================================
// Sidebar
// ============================================================================
function Sidebar({ branches, activeWorkspace }) {
  const [open, setOpen] = useState({ workspace: true, branches: true, remotes: true });
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  const Section = ({ id, icon: Icon, label, count, children }) => (
    <div className="sb-section">
      <button className="sb-section-head" onClick={() => toggle(id)}>
        <ChevronDown size={9} className={open[id] ? '' : 'sb-rot'} />
        <Icon size={11} strokeWidth={2} className="sb-section-icon" />
        <span>{label}</span>
        {count !== undefined && <span className="sb-count">{count}</span>}
      </button>
      {open[id] && <div className="sb-section-body">{children}</div>}
    </div>
  );
  const WSItem = ({ id, icon: Icon, label }) => (
    <div className={`sb-item ${activeWorkspace === id ? 'sb-item-active' : ''}`}>
      <Icon size={11} strokeWidth={2} /><span>{label}</span>
    </div>
  );

  return (
    <aside className="sidebar">
      <Section id="workspace" icon={Box} label="WORKSPACE">
        <WSItem id="status" icon={ListTree} label="File status" />
        <WSItem id="history" icon={GitCommitVertical} label="History" />
        <WSItem id="search" icon={Search} label="Search" />
      </Section>
      <Section id="branches" icon={GitBranch} label="BRANCHES" count={branches.length}>
        {branches.map(b => (
          <div key={b} className={`sb-item sb-branch ${b === 'HEAD' ? 'sb-item-active' : ''}`}>
            {b === 'HEAD' ? <CircleDot size={10} /> : <GitBranch size={10} strokeWidth={2} />}
            <span>{b}</span>
          </div>
        ))}
        {branches.length === 0 && <div className="sb-empty">no refs in response</div>}
      </Section>
      <Section id="remotes" icon={Server} label="REMOTES">
        <div className="sb-item"><Server size={11} strokeWidth={2} /><span>origin</span></div>
      </Section>
    </aside>
  );
}

// ============================================================================
// Commit Graph
// ============================================================================
function CommitGraph({ commits, refs, selectedSha, onSelect }) {
  return (
    <div className="cg-pane">
      <div className="cg-head">
        <div className="cg-h cg-h-graph">Graph</div>
        <div className="cg-h cg-h-desc">Description</div>
        <div className="cg-h cg-h-sha">Commit</div>
        <div className="cg-h cg-h-author">Author</div>
        <div className="cg-h cg-h-date">Date</div>
      </div>
      <div className="cg-body">
        {commits.map((c, i) => {
          const isLast = i === commits.length - 1;
          const isFirst = i === 0;
          const refsHere = refs[c.sha] || [];
          const msg = (c.commit?.message || '').split('\n')[0];
          const author = c.author?.login || c.commit?.author?.name || 'unknown';
          const date = c.commit?.author?.date;
          const selected = c.sha === selectedSha;
          return (
            <button key={c.sha} className={`cg-row ${selected ? 'cg-row-sel' : ''}`} onClick={() => onSelect(c.sha)}>
              <div className="cg-c cg-c-graph">
                {!isFirst && <div className="cg-line cg-line-up" />}
                {!isLast && <div className="cg-line cg-line-down" />}
                <div className={`cg-dot ${selected ? 'cg-dot-sel' : ''}`} />
              </div>
              <div className="cg-c cg-c-desc">
                {refsHere.map(r => (
                  <span key={r} className={`cg-ref ${r === 'HEAD' ? 'cg-ref-head' : ''}`}>
                    {r === 'HEAD' ? <><CircleDot size={9} /> {r}</> : <><GitBranch size={9} strokeWidth={2.5} /> {r}</>}
                  </span>
                ))}
                <span className="cg-msg">{msg}</span>
              </div>
              <div className="cg-c cg-c-sha">{shortSha(c.sha)}</div>
              <div className="cg-c cg-c-author">{author}</div>
              <div className="cg-c cg-c-date">{relTime(date)}</div>
            </button>
          );
        })}
        {commits.length === 0 && <div className="cg-empty">No commits in output</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Request echo
// ============================================================================
function RequestEcho({ operation, input }) {
  if (!input) return null;
  let display = { ...input };
  if (operation === 'create_or_update_file' && display.content !== undefined) {
    display.content = `‹${typeof display.content === 'string' ? display.content.length : '?'} chars›`;
  }
  if (operation === 'push_files' && Array.isArray(display.files)) {
    display.files = `[${display.files.length} file${display.files.length === 1 ? '' : 's'}]`;
  }
  const args = Object.entries(display)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => {
      const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
      const short = val.length > 36 ? val.slice(0, 33) + '…' : val;
      return <span key={k} className="echo-arg"><span className="echo-k">{k}</span>=<span className="echo-v">{short}</span></span>;
    });
  return (
    <div className="req-echo">
      <span className="echo-prompt">$</span>{' '}
      <span className="echo-op">{operation}</span>{' '}
      {args}
    </div>
  );
}

// ============================================================================
// Detail variants
// ============================================================================
function CommitDetail({ commit }) {
  if (!commit) return <div className="dp-empty">Select a commit</div>;
  const msg = commit.commit?.message || '';
  const [subject, ...bodyLines] = msg.split('\n');
  const body = bodyLines.join('\n').trim();
  const gitAuthor = commit.commit?.author;
  const gitCommitter = commit.commit?.committer;
  const ghAuthor = commit.author;       // GitHub user (may be null for non-mapped emails)
  const ghCommitter = commit.committer;
  const sameAC = gitAuthor?.email === gitCommitter?.email && gitAuthor?.date === gitCommitter?.date;
  const stats = commit.stats;
  const files = commit.files || [];
  const verification = commit.commit?.verification;

  return (
    <>
      <div className="dp-subject">{subject}</div>
      {body && <div className="dp-body">{body}</div>}
      <div className="dp-meta">
        <div className="dp-meta-row">
          <span className="dp-k"><Hash size={10} /> commit</span>
          <span className="dp-v dp-mono">{commit.sha}</span>
        </div>
        {commit.parents?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k"><GitCommitVertical size={10} /> parent{commit.parents.length > 1 ? 's' : ''}</span>
            <span className="dp-v dp-mono">{commit.parents.map(p => shortSha(p.sha)).join(', ')}{commit.parents.length > 1 && ' (merge)'}</span>
          </div>
        )}
        {gitAuthor && (
          <div className="dp-meta-row">
            <span className="dp-k"><User size={10} /> author</span>
            <span className="dp-v">
              {gitAuthor.name} <span className="dp-dim">&lt;{gitAuthor.email}&gt;</span>
              {ghAuthor?.login && <span className="dp-dim"> · @{ghAuthor.login}</span>}
            </span>
          </div>
        )}
        {!sameAC && gitCommitter && (
          <div className="dp-meta-row">
            <span className="dp-k"><User size={10} /> committer</span>
            <span className="dp-v">
              {gitCommitter.name} <span className="dp-dim">&lt;{gitCommitter.email}&gt;</span>
              {ghCommitter?.login && ghCommitter.login !== ghAuthor?.login && <span className="dp-dim"> · @{ghCommitter.login}</span>}
            </span>
          </div>
        )}
        {gitAuthor?.date && (
          <div className="dp-meta-row">
            <span className="dp-k"><Calendar size={10} /> authored</span>
            <span className="dp-v">{new Date(gitAuthor.date).toLocaleString()}</span>
          </div>
        )}
        {!sameAC && gitCommitter?.date && (
          <div className="dp-meta-row">
            <span className="dp-k"><Calendar size={10} /> committed</span>
            <span className="dp-v">{new Date(gitCommitter.date).toLocaleString()}</span>
          </div>
        )}
        {commit.commit?.tree?.sha && (
          <div className="dp-meta-row">
            <span className="dp-k">tree</span>
            <span className="dp-v dp-mono">{shortSha(commit.commit.tree.sha)}</span>
          </div>
        )}
        {commit.commit?.comment_count !== undefined && commit.commit.comment_count > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">comments</span>
            <span className="dp-v">{commit.commit.comment_count}</span>
          </div>
        )}
        {verification && (
          <div className="dp-meta-row">
            <span className="dp-k"><ShieldCheck size={10} /> signed</span>
            <span className="dp-v">
              {verification.verified
                ? <><span className="dp-pill dp-pill-ok">verified</span><span className="dp-dim"> · {verification.reason}</span></>
                : <><span className="dp-pill dp-pill-no">unverified</span>{verification.reason && <span className="dp-dim"> · {verification.reason}</span>}</>}
            </span>
          </div>
        )}
        {commit.html_url && (
          <div className="dp-meta-row">
            <span className="dp-k">link</span>
            <span className="dp-v"><ExtLink href={commit.html_url}>View on GitHub</ExtLink></span>
          </div>
        )}
      </div>
      {stats && (
        <div className="dp-stats">
          <span className="dp-stat-add">+{stats.additions}</span>
          <span className="dp-stat-del">−{stats.deletions}</span>
          <span className="dp-stat-total">across {files.length} file{files.length === 1 ? '' : 's'}</span>
        </div>
      )}
      {files.length > 0 && (
        <div className="dp-files">
          <div className="dp-files-head">FILES</div>
          {files.map(f => {
            const g = statusGlyph(f.status);
            return (
              <div key={f.filename} className="dp-file">
                <span className={`dp-fstatus ${g.cls}`}>{g.ch}</span>
                <span className="dp-fname">{f.filename}</span>
                <span className="dp-fstat">
                  <span className="fl-add">+{f.additions}</span>{' '}
                  <span className="fl-del">−{f.deletions}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function RepoDetail({ repo }) {
  return (
    <>
      <div className="dp-subject">{repo.full_name || repo.name}</div>
      {repo.description && <div className="dp-body">{repo.description}</div>}
      <div className="dp-meta">
        <div className="dp-meta-row">
          <span className="dp-k">visibility</span>
          <span className="dp-v">
            <span className={`dp-pill ${repo.private ? 'dp-pill-warn' : 'dp-pill-ok'}`}>
              {repo.visibility || (repo.private ? 'private' : 'public')}
            </span>
            {repo.fork && <span className="dp-pill dp-pill-info" style={{ marginLeft: 4 }}>fork</span>}
            {repo.archived && <span className="dp-pill dp-pill-warn" style={{ marginLeft: 4 }}><Archive size={9} /> archived</span>}
            {repo.disabled && <span className="dp-pill dp-pill-no" style={{ marginLeft: 4 }}>disabled</span>}
          </span>
        </div>
        {repo.default_branch && (
          <div className="dp-meta-row">
            <span className="dp-k">default branch</span>
            <span className="dp-v dp-mono">{repo.default_branch}</span>
          </div>
        )}
        {repo.language && (
          <div className="dp-meta-row">
            <span className="dp-k">language</span>
            <span className="dp-v">{repo.language}</span>
          </div>
        )}
        {repo.topics?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">topics</span>
            <span className="dp-v">{repo.topics.map(t => <span key={t} className="dp-chip dp-chip-info">{t}</span>)}</span>
          </div>
        )}
        {repo.license && (
          <div className="dp-meta-row">
            <span className="dp-k">license</span>
            <span className="dp-v">{repo.license.spdx_id || repo.license.key} <span className="dp-dim">— {repo.license.name}</span></span>
          </div>
        )}
        {repo.homepage && (
          <div className="dp-meta-row">
            <span className="dp-k">homepage</span>
            <span className="dp-v dp-mono dp-trunc">{repo.homepage}</span>
          </div>
        )}
        {repo.stargazers_count !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">★ / ⑂ / 👁</span>
            <span className="dp-v">
              {fmtNum(repo.stargazers_count)} stars · {fmtNum(repo.forks_count)} forks
              {repo.watchers_count !== undefined && ` · ${fmtNum(repo.watchers_count)} watchers`}
            </span>
          </div>
        )}
        {repo.open_issues_count !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">open issues</span>
            <span className="dp-v">{fmtNum(repo.open_issues_count)}</span>
          </div>
        )}
        {repo.size !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">size</span>
            <span className="dp-v">{fmtNum(repo.size)} KB</span>
          </div>
        )}
        {repo.created_at && (
          <div className="dp-meta-row">
            <span className="dp-k">created</span>
            <span className="dp-v">{new Date(repo.created_at).toLocaleDateString()}</span>
          </div>
        )}
        {repo.updated_at && (
          <div className="dp-meta-row">
            <span className="dp-k">updated</span>
            <span className="dp-v">{relTime(repo.updated_at)}</span>
          </div>
        )}
        {repo.pushed_at && (
          <div className="dp-meta-row">
            <span className="dp-k">last push</span>
            <span className="dp-v">{relTime(repo.pushed_at)}</span>
          </div>
        )}
        {repo.clone_url && (
          <div className="dp-meta-row">
            <span className="dp-k">clone url</span>
            <span className="dp-v dp-mono dp-trunc">{repo.clone_url}</span>
          </div>
        )}
        {repo.html_url && (
          <div className="dp-meta-row">
            <span className="dp-k">link</span>
            <span className="dp-v"><ExtLink href={repo.html_url} /></span>
          </div>
        )}
      </div>
    </>
  );
}

function IssueDetail({ issue, isPR }) {
  // PR detection — issues with a `pull_request` field are PRs
  isPR = isPR || !!issue.pull_request;
  // State pill — distinguish merged PRs
  let stateLabel = issue.state;
  let stateCls = issue.state === 'open' ? 'dp-pill-ok' : 'dp-pill-no';
  if (isPR && issue.merged) {
    stateLabel = 'merged';
    stateCls = 'dp-pill-merged';
  } else if (issue.state === 'closed' && issue.state_reason === 'not_planned') {
    stateLabel = 'closed (not planned)';
  } else if (issue.state === 'closed' && issue.state_reason === 'completed') {
    stateLabel = 'closed (completed)';
  }

  return (
    <>
      <div className="dp-subject">
        {isPR
          ? <GitPullRequest size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          : <CircleDot size={14} style={{ verticalAlign: -2, marginRight: 4 }} />}
        #{issue.number} {issue.title}
        {issue.locked && <Lock size={12} style={{ verticalAlign: -1, marginLeft: 6, color: '#8a5a00' }} />}
      </div>
      <div className="dp-meta">
        <div className="dp-meta-row">
          <span className="dp-k">state</span>
          <span className="dp-v">
            <span className={`dp-pill ${stateCls}`}>{stateLabel}</span>
            {issue.draft && <span className="dp-pill dp-pill-info" style={{ marginLeft: 4 }}>draft</span>}
            {issue.author_association && issue.author_association !== 'NONE' && (
              <span className="dp-dim" style={{ marginLeft: 6 }}>{issue.author_association.toLowerCase().replace(/_/g, ' ')}</span>
            )}
          </span>
        </div>
        {issue.user && (
          <div className="dp-meta-row">
            <span className="dp-k">opened by</span>
            <span className="dp-v">@{issue.user.login}</span>
          </div>
        )}
        {isPR && issue.head && (
          <div className="dp-meta-row">
            <span className="dp-k">branch</span>
            <span className="dp-v dp-mono">{issue.head.ref || issue.head} → {issue.base?.ref || issue.base}</span>
          </div>
        )}
        {isPR && issue.commits !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">commits</span>
            <span className="dp-v">{issue.commits} commit{issue.commits === 1 ? '' : 's'}</span>
          </div>
        )}
        {isPR && issue.changed_files !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">changes</span>
            <span className="dp-v">
              <span className="fl-add">+{fmtNum(issue.additions)}</span>{' '}
              <span className="fl-del">−{fmtNum(issue.deletions)}</span>{' '}
              <span className="dp-dim">across {issue.changed_files} files</span>
            </span>
          </div>
        )}
        {isPR && issue.merged !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">merge</span>
            <span className="dp-v">
              {issue.merged
                ? <>
                    <span className="dp-pill dp-pill-merged">merged</span>
                    {issue.merged_by?.login && <span className="dp-dim"> by @{issue.merged_by.login}</span>}
                    {issue.merged_at && <span className="dp-dim"> · {relTime(issue.merged_at)}</span>}
                  </>
                : <>
                    <span className={`dp-pill ${issue.mergeable ? 'dp-pill-ok' : 'dp-pill-no'}`}>
                      {issue.mergeable === null ? 'computing' : (issue.mergeable ? 'mergeable' : 'conflicts')}
                    </span>
                    {issue.mergeable_state && issue.mergeable_state !== 'unknown' && (
                      <span className="dp-dim"> · state: {issue.mergeable_state}</span>
                    )}
                  </>}
            </span>
          </div>
        )}
        {isPR && issue.requested_reviewers?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">reviewers</span>
            <span className="dp-v">{issue.requested_reviewers.map(r => `@${r.login}`).join(', ')}</span>
          </div>
        )}
        {issue.labels?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">labels</span>
            <span className="dp-v">{issue.labels.map((l, i) => {
              const name = typeof l === 'string' ? l : l.name;
              const color = typeof l === 'object' && l.color ? `#${l.color}` : null;
              return (
                <span key={i} className="dp-chip" style={color ? { borderColor: color, color } : undefined}>{name}</span>
              );
            })}</span>
          </div>
        )}
        {issue.assignees?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">assignees</span>
            <span className="dp-v">{issue.assignees.map(a => `@${typeof a === 'string' ? a : a.login}`).join(', ')}</span>
          </div>
        )}
        {issue.milestone && (
          <div className="dp-meta-row">
            <span className="dp-k">milestone</span>
            <span className="dp-v">
              {issue.milestone.title}
              {issue.milestone.due_on && <span className="dp-dim"> · due {new Date(issue.milestone.due_on).toLocaleDateString()}</span>}
            </span>
          </div>
        )}
        {issue.comments !== undefined && (
          <div className="dp-meta-row">
            <span className="dp-k">comments</span>
            <span className="dp-v">
              {issue.comments}
              {isPR && issue.review_comments !== undefined && <span className="dp-dim"> · {issue.review_comments} review</span>}
              <ReactionsRow reactions={issue.reactions} />
            </span>
          </div>
        )}
        {issue.created_at && (
          <div className="dp-meta-row">
            <span className="dp-k">opened</span>
            <span className="dp-v">{relTime(issue.created_at)}</span>
          </div>
        )}
        {issue.closed_at && (
          <div className="dp-meta-row">
            <span className="dp-k">closed</span>
            <span className="dp-v">{relTime(issue.closed_at)}{issue.closed_by?.login && <span className="dp-dim"> by @{issue.closed_by.login}</span>}</span>
          </div>
        )}
        {issue.updated_at && issue.updated_at !== issue.created_at && (
          <div className="dp-meta-row">
            <span className="dp-k">updated</span>
            <span className="dp-v">{relTime(issue.updated_at)}</span>
          </div>
        )}
        {issue.html_url && (
          <div className="dp-meta-row">
            <span className="dp-k">link</span>
            <span className="dp-v"><ExtLink href={issue.html_url} /></span>
          </div>
        )}
      </div>
      {issue.body && (
        <div className="dp-issue-body">
          <div className="dp-files-head">BODY</div>
          <div className="dp-body-text">{issue.body}</div>
        </div>
      )}
    </>
  );
}

function ListDetail({ items, label, totalCount, incomplete, renderItem, icon: Icon }) {
  return (
    <>
      <div className="dp-subject">
        {Icon && <Icon size={14} style={{ verticalAlign: -2, marginRight: 4 }} />}
        {label} · {fmtNum(totalCount ?? items.length)} {totalCount !== undefined && totalCount !== items.length && <span className="dp-dim">(showing {items.length})</span>}
        {incomplete && <span className="dp-pill dp-pill-warn" style={{ marginLeft: 8 }}>incomplete</span>}
      </div>
      <div className="dp-files dp-files-list">
        {items.map((it, i) => (
          <div key={i} className="dp-file">{renderItem ? renderItem(it) : <span className="dp-fname">{JSON.stringify(it).slice(0, 80)}</span>}</div>
        ))}
        {items.length === 0 && <div className="dp-empty-inline">No items</div>}
      </div>
    </>
  );
}

function FileOpDetail({ output, isPush, fileCount, input }) {
  const c = output?.content || {};
  const cm = output?.commit || output || {};
  return (
    <>
      <div className="dp-subject">
        {isPush ? <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> : <FilePlus2 size={14} style={{ verticalAlign: -2, marginRight: 4 }} />}
        {isPush ? `Pushed ${fileCount ?? '?'} file${fileCount === 1 ? '' : 's'}` : `Wrote ${c.path || c.name || '(file)'}`}
      </div>
      <div className="dp-meta">
        {!isPush && c.path && (
          <div className="dp-meta-row"><span className="dp-k">path</span><span className="dp-v dp-mono">{c.path}</span></div>
        )}
        {!isPush && c.size !== undefined && (
          <div className="dp-meta-row"><span className="dp-k">size</span><span className="dp-v">{fmtNum(c.size)} bytes</span></div>
        )}
        {!isPush && c.sha && (
          <div className="dp-meta-row"><span className="dp-k">blob sha</span><span className="dp-v dp-mono">{shortSha(c.sha)}</span></div>
        )}
        {!isPush && c.type && (
          <div className="dp-meta-row"><span className="dp-k">type</span><span className="dp-v dp-mono">{c.type}</span></div>
        )}
        {isPush && Array.isArray(input?.files) && (
          <div className="dp-meta-row">
            <span className="dp-k">files</span>
            <span className="dp-v dp-mono dp-trunc">{input.files.map(f => f.path).join(', ')}</span>
          </div>
        )}
        {cm.sha && (
          <div className="dp-meta-row"><span className="dp-k">commit</span><span className="dp-v dp-mono">{shortSha(cm.sha)}</span></div>
        )}
        {cm.message && (
          <div className="dp-meta-row"><span className="dp-k">message</span><span className="dp-v">{cm.message}</span></div>
        )}
        {cm.author && (
          <div className="dp-meta-row">
            <span className="dp-k">author</span>
            <span className="dp-v">{cm.author.name} <span className="dp-dim">&lt;{cm.author.email}&gt;</span></span>
          </div>
        )}
        {cm.author?.date && (
          <div className="dp-meta-row"><span className="dp-k">date</span><span className="dp-v">{relTime(cm.author.date)}</span></div>
        )}
        {cm.parents?.length > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">parent{cm.parents.length > 1 ? 's' : ''}</span>
            <span className="dp-v dp-mono">{cm.parents.map(p => shortSha(p.sha)).join(', ')}</span>
          </div>
        )}
        {cm.verification && (
          <div className="dp-meta-row">
            <span className="dp-k">signed</span>
            <span className="dp-v">
              {cm.verification.verified
                ? <span className="dp-pill dp-pill-ok">verified</span>
                : <span className="dp-pill dp-pill-no">unverified</span>}
            </span>
          </div>
        )}
        {(c.html_url || cm.html_url) && (
          <div className="dp-meta-row">
            <span className="dp-k">link</span>
            <span className="dp-v">
              {c.html_url && <ExtLink href={c.html_url}>View file</ExtLink>}
              {c.html_url && cm.html_url && <span className="dp-dim"> · </span>}
              {cm.html_url && <ExtLink href={cm.html_url}>View commit</ExtLink>}
            </span>
          </div>
        )}
        {c.download_url && (
          <div className="dp-meta-row">
            <span className="dp-k">raw</span>
            <span className="dp-v dp-mono dp-trunc">{c.download_url}</span>
          </div>
        )}
      </div>
    </>
  );
}

function ContentsDetail({ output }) {
  if (Array.isArray(output)) {
    return (
      <>
        <div className="dp-subject">
          <Folder size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          Directory · {output.length} entries
        </div>
        <div className="dp-files dp-files-list">
          {output.map((e, i) => (
            <div key={i} className="dp-file">
              <span className={`dp-fstatus ${e.type === 'dir' ? 'st-R' : e.type === 'symlink' ? 'st-C' : 'st-U'}`}>
                {e.type === 'dir' ? 'D' : e.type === 'symlink' ? 'L' : 'F'}
              </span>
              <span className="dp-fname">{e.name}</span>
              <span className="dp-fstat">
                {e.size !== undefined && e.type === 'file' ? `${fmtNum(e.size)} B` : ''}
              </span>
            </div>
          ))}
        </div>
      </>
    );
  }
  const decoded = (() => {
    if (output?.encoding === 'base64' && typeof output?.content === 'string') {
      try { return atob(output.content.replace(/\s/g, '')); } catch { return null; }
    }
    return null;
  })();
  return (
    <>
      <div className="dp-subject">
        <FileText size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
        {output?.path || output?.name}
      </div>
      <div className="dp-meta">
        {output?.type && (
          <div className="dp-meta-row"><span className="dp-k">type</span><span className="dp-v dp-mono">{output.type}</span></div>
        )}
        {output?.size !== undefined && (
          <div className="dp-meta-row"><span className="dp-k">size</span><span className="dp-v">{fmtNum(output.size)} bytes</span></div>
        )}
        {output?.sha && (
          <div className="dp-meta-row"><span className="dp-k">blob sha</span><span className="dp-v dp-mono">{shortSha(output.sha)}</span></div>
        )}
        {output?.encoding && (
          <div className="dp-meta-row"><span className="dp-k">encoding</span><span className="dp-v dp-mono">{output.encoding}</span></div>
        )}
        {output?.html_url && (
          <div className="dp-meta-row"><span className="dp-k">link</span><span className="dp-v"><ExtLink href={output.html_url} /></span></div>
        )}
        {output?.download_url && (
          <div className="dp-meta-row"><span className="dp-k">raw</span><span className="dp-v dp-mono dp-trunc">{output.download_url}</span></div>
        )}
      </div>
      {decoded && (
        <div className="dp-files">
          <div className="dp-files-head">CONTENT</div>
          <pre className="dp-raw">{decoded.slice(0, 4000)}{decoded.length > 4000 ? '\n…' : ''}</pre>
        </div>
      )}
    </>
  );
}

function BranchRefDetail({ output }) {
  const name = output?.ref?.replace('refs/heads/', '').replace('refs/tags/', '') || '?';
  return (
    <>
      <div className="dp-subject"><GitBranch size={14} style={{ verticalAlign: -2, marginRight: 4 }} />{name}</div>
      <div className="dp-meta">
        <div className="dp-meta-row"><span className="dp-k">ref</span><span className="dp-v dp-mono">{output?.ref}</span></div>
        <div className="dp-meta-row"><span className="dp-k">target sha</span><span className="dp-v dp-mono">{shortSha(output?.object?.sha)}</span></div>
        <div className="dp-meta-row"><span className="dp-k">target type</span><span className="dp-v dp-mono">{output?.object?.type}</span></div>
        {output?.node_id && (
          <div className="dp-meta-row"><span className="dp-k">node id</span><span className="dp-v dp-mono dp-trunc">{output.node_id}</span></div>
        )}
        {output?.url && (
          <div className="dp-meta-row"><span className="dp-k">api url</span><span className="dp-v dp-mono dp-trunc">{output.url}</span></div>
        )}
      </div>
    </>
  );
}

function CommentDetail({ output, input }) {
  const edited = output?.updated_at && output.created_at && output.updated_at !== output.created_at;
  return (
    <>
      <div className="dp-subject">
        <MessageSquare size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
        Comment posted{input?.issue_number ? ` on #${input.issue_number}` : ''}
      </div>
      <div className="dp-meta">
        {output?.id !== undefined && (
          <div className="dp-meta-row"><span className="dp-k">comment id</span><span className="dp-v dp-mono">{output.id}</span></div>
        )}
        {output?.user?.login && (
          <div className="dp-meta-row">
            <span className="dp-k">author</span>
            <span className="dp-v">
              @{output.user.login}
              {output.author_association && output.author_association !== 'NONE' && (
                <span className="dp-dim"> · {output.author_association.toLowerCase().replace(/_/g, ' ')}</span>
              )}
            </span>
          </div>
        )}
        {output?.created_at && (
          <div className="dp-meta-row"><span className="dp-k">posted</span><span className="dp-v">{relTime(output.created_at)}</span></div>
        )}
        {edited && (
          <div className="dp-meta-row">
            <span className="dp-k">edited</span>
            <span className="dp-v">{relTime(output.updated_at)}</span>
          </div>
        )}
        {output?.reactions?.total_count > 0 && (
          <div className="dp-meta-row">
            <span className="dp-k">reactions</span>
            <span className="dp-v"><ReactionsRow reactions={output.reactions} /></span>
          </div>
        )}
        {output?.html_url && (
          <div className="dp-meta-row"><span className="dp-k">link</span><span className="dp-v"><ExtLink href={output.html_url} /></span></div>
        )}
      </div>
      {output?.body && (
        <div className="dp-issue-body">
          <div className="dp-files-head">BODY</div>
          <div className="dp-body-text">{output.body}</div>
        </div>
      )}
    </>
  );
}

function DeleteDetail({ input }) {
  return (
    <>
      <div className="dp-subject">
        <Trash2 size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
        Repository deleted
      </div>
      <div className="dp-meta">
        <div className="dp-meta-row">
          <span className="dp-k">target</span>
          <span className="dp-v dp-mono">{input?.owner}/{input?.repo}</span>
        </div>
      </div>
      <div className="dp-hint">
        <code>delete_repository</code> returns <strong>204 No Content</strong> with an empty body — no output to render. The breadcrumb above reflects the input.
      </div>
    </>
  );
}

function RawDetail({ data }) {
  return (
    <>
      <div className="dp-subject">Output</div>
      <pre className="dp-raw">{data === null || data === undefined ? '(empty)' : JSON.stringify(data, null, 2)}</pre>
    </>
  );
}

// ============================================================================
// Main exported component
// ============================================================================
export function XcodeVCS({ operation, input, output }) {
  const knownOp = OPERATIONS.has(operation);
  const [selectedSha, setSelectedSha] = useState(null);

  useEffect(() => { setSelectedSha(null); }, [operation, input, output]);

  const commits = useMemo(() => {
    if (operation === 'list_commits' && Array.isArray(output)) return output;
    return [];
  }, [operation, output]);

  const isCommitView = commits.length > 0;
  const effectiveSha = selectedSha || commits[0]?.sha || null;
  const selectedCommit = commits.find(c => c.sha === effectiveSha);

  const refs = useMemo(() => {
    const r = {};
    if (commits[0]) r[commits[0].sha] = ['HEAD'];
    if (input?.sha && commits.length > 0) {
      const m = commits.find(c => c.sha === input.sha || c.sha.startsWith(input.sha));
      if (m && m.sha !== commits[0]?.sha) {
        r[m.sha] = [...(r[m.sha] || []), input.sha];
      }
    }
    return r;
  }, [commits, input]);
  const branches = useMemo(() => Object.values(refs).flat(), [refs]);

  const path = buildPath(operation, input);

  const activeWorkspace = (() => {
    if (operation === 'list_commits') return 'history';
    if (/^search_/.test(operation)) return 'search';
    if (operation === 'create_or_update_file' || operation === 'push_files' || operation === 'get_file_contents') return 'status';
    return 'history';
  })();

  const itemCount = (() => {
    if (Array.isArray(output)) return output.length;
    if (output?.items?.length !== undefined) return output.items.length;
    return null;
  })();

  const detailNode = (() => {
    if (!knownOp) {
      return (
        <>
          <div className="dp-subject">Unknown operation</div>
          <div className="dp-hint">
            <code>{operation || '(none)'}</code> isn't in the schema. Pass one of:
            <span className="dp-mono"> {Array.from(OPERATIONS).join(', ')}</span>
          </div>
          <pre className="dp-raw" style={{ marginTop: 12 }}>{JSON.stringify({ input, output }, null, 2)}</pre>
        </>
      );
    }
    switch (operation) {
      case 'list_commits':
        return <CommitDetail commit={selectedCommit} />;
      case 'create_repository':
      case 'fork_repository':
        return <RepoDetail repo={output} />;
      case 'create_issue':
      case 'update_issue':
      case 'get_issue':
        return <IssueDetail issue={output} />;
      case 'create_pull_request':
        return <IssueDetail issue={output} isPR />;
      case 'list_issues':
        return <ListDetail
          label="Issues"
          icon={CircleDot}
          items={Array.isArray(output) ? output : []}
          renderItem={(it) => {
            const isPR = !!it.pull_request;
            return (
              <>
                {isPR
                  ? <GitPullRequest size={11} className={it.state === 'open' ? 'dp-state-open' : 'dp-state-closed'} style={{ marginRight: 6 }} />
                  : <CircleDot size={11} className={it.state === 'open' ? 'dp-state-open' : 'dp-state-closed'} style={{ marginRight: 6 }} />}
                <span className="dp-fname">#{it.number} {it.title}</span>
                <span className="dp-fstat dp-dim">@{it.user?.login}{it.comments ? ` · 💬 ${it.comments}` : ''}</span>
              </>
            );
          }}
        />;
      case 'search_repositories':
        return <ListDetail
          label="Repositories"
          icon={Box}
          items={output?.items || []}
          totalCount={output?.total_count}
          incomplete={output?.incomplete_results}
          renderItem={(it) => (
            <>
              <span className="dp-fname">{it.full_name}</span>
              <span className="dp-fstat dp-dim">★ {fmtNum(it.stargazers_count)}{it.language ? ` · ${it.language}` : ''}{it.score ? ` · score ${it.score.toFixed(2)}` : ''}</span>
            </>
          )}
        />;
      case 'search_users':
        return <ListDetail
          label="Users"
          icon={User}
          items={output?.items || []}
          totalCount={output?.total_count}
          incomplete={output?.incomplete_results}
          renderItem={(it) => (
            <>
              <User size={11} style={{ marginRight: 6 }} />
              <span className="dp-fname">@{it.login}</span>
              <span className="dp-fstat dp-dim">{it.type}{it.score ? ` · score ${it.score.toFixed(2)}` : ''}</span>
            </>
          )}
        />;
      case 'search_code':
        return <ListDetail
          label="Code matches"
          icon={FileText}
          items={output?.items || []}
          totalCount={output?.total_count}
          incomplete={output?.incomplete_results}
          renderItem={(it) => (
            <>
              <FileText size={11} style={{ marginRight: 6 }} />
              <span className="dp-fname dp-mono">{it.path}</span>
              <span className="dp-fstat dp-dim">{it.repository?.full_name}{it.score ? ` · score ${it.score.toFixed(2)}` : ''}</span>
            </>
          )}
        />;
      case 'search_issues':
        return <ListDetail
          label="Issues / PRs"
          icon={CircleDot}
          items={output?.items || []}
          totalCount={output?.total_count}
          incomplete={output?.incomplete_results}
          renderItem={(it) => (
            <>
              {it.pull_request
                ? <GitPullRequest size={11} className={it.state === 'open' ? 'dp-state-open' : 'dp-state-closed'} style={{ marginRight: 6 }} />
                : <CircleDot size={11} className={it.state === 'open' ? 'dp-state-open' : 'dp-state-closed'} style={{ marginRight: 6 }} />}
              <span className="dp-fname">#{it.number} {it.title}</span>
              <span className="dp-fstat dp-dim">{it.state}{it.comments ? ` · 💬 ${it.comments}` : ''}</span>
            </>
          )}
        />;
      case 'get_file_contents':
        return <ContentsDetail output={output} />;
      case 'create_or_update_file':
        return <FileOpDetail output={output} />;
      case 'push_files':
        return <FileOpDetail output={output} isPush fileCount={input?.files?.length} input={input} />;
      case 'create_branch':
        return <BranchRefDetail output={output} />;
      case 'add_issue_comment':
        return <CommentDetail output={output} input={input} />;
      case 'delete_repository':
        return <DeleteDetail input={input} />;
      default:
        return <RawDetail data={output} />;
    }
  })();

  return (
    <>
      <style>{styles}</style>
      <div className="xv-window">
        <div className="xv-titlebar">
          <div className="xv-traffic">
            <span className="tl tl-r" /><span className="tl tl-y" /><span className="tl tl-g" />
          </div>
          <div className="xv-title-path" title={path}>
            <Folder size={11} />
            <span>{path}</span>
          </div>
        </div>
        <div className="main">
          <Sidebar branches={branches} activeWorkspace={activeWorkspace} />
          <div className="content">
            {isCommitView ? (
              <>
                <CommitGraph commits={commits} refs={refs} selectedSha={effectiveSha} onSelect={setSelectedSha} />
                <div className="detail">
                  <RequestEcho operation={operation} input={input} />
                  <div className="dp-pad">
                    {detailNode}
                    <RawFooter data={selectedCommit ?? output} label="commit" />
                  </div>
                </div>
              </>
            ) : (
              <div className="detail detail-full">
                <RequestEcho operation={operation} input={input} />
                <div className="dp-pad">
                  {detailNode}
                  {operation !== 'delete_repository' && <RawFooter data={output} />}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="statusbar">
          <span className="sb-dot" />
          <span>op: <strong>{operation || '—'}</strong></span>
          {!knownOp && operation && <><span className="sb-sep">·</span><span className="sb-warn">unknown</span></>}
          {itemCount !== null && itemCount !== undefined && (
            <>
              <span className="sb-sep">·</span>
              <span>{fmtNum(itemCount)} item{itemCount === 1 ? '' : 's'}</span>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// DEMO — sample (operation, input, output) triples with realistic schemas
// ============================================================================
const SAMPLES = {
  list_commits: {
    operation: 'list_commits',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', sha: 'reworkUI', perPage: 10 },
    output: [
      {
        sha: "3cdb4c6f8e2a1d7c9b3e5f8a2c4d6e8f1a3b5c7d",
        node_id: "C_kwDOH3xyz123",
        commit: {
          author: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T09:27:00Z" },
          committer: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T09:27:00Z" },
          message: "final changes\n\nAdjusted spacing in ProductsView and renamed productStore variable\nfor clarity. No behavioural changes.",
          tree: { sha: "a1b2c3d4e5f6789", url: "https://api.github.com/..." },
          comment_count: 0,
          verification: { verified: true, reason: "valid", signature: "—", payload: "—" }
        },
        url: "https://api.github.com/repos/stevencurtis/TestVersionControl/commits/3cdb4c6f...",
        html_url: "https://github.com/stevencurtis/TestVersionControl/commit/3cdb4c6f...",
        comments_url: "https://api.github.com/repos/stevencurtis/TestVersionControl/commits/3cdb4c6f.../comments",
        author: { login: "stevencurtis", id: 4287, type: "User", avatar_url: "https://avatars.githubusercontent.com/u/4287" },
        committer: { login: "stevencurtis", id: 4287, type: "User" },
        parents: [{ sha: "005bd203a7c5e9f1b3d7a9c5e1f3b5a7c9d1e3f5", url: "...", html_url: "..." }]
      },
      {
        sha: "005bd203a7c5e9f1b3d7a9c5e1f3b5a7c9d1e3f5",
        commit: {
          author: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:57:00Z" },
          committer: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:57:00Z" },
          message: "Update test data and UI.",
          comment_count: 1,
          verification: { verified: false, reason: "unsigned" }
        },
        html_url: "https://github.com/stevencurtis/TestVersionControl/commit/005bd203...",
        author: { login: "stevencurtis" },
        committer: { login: "stevencurtis" },
        parents: [{ sha: "735b957c2add4006bd3cf16d16e97cf3eca329f1" }]
      },
      {
        sha: "735b957c2add4006bd3cf16d16e97cf3eca329f1",
        commit: {
          author: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:40:00Z" },
          committer: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:40:00Z" },
          message: "Add files",
          verification: { verified: false, reason: "unsigned" }
        },
        html_url: "https://github.com/stevencurtis/TestVersionControl/commit/735b957c...",
        author: { login: "stevencurtis" },
        parents: [{ sha: "7d33728b1c0e9f3a5b7d9c1e3f5a7b9c1d3e5f7a" }]
      },
      {
        sha: "7d33728b1c0e9f3a5b7d9c1e3f5a7b9c1d3e5f7a",
        commit: {
          author: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:27:00Z" },
          committer: { name: "Steven Curtis", email: "s@curtis.dev", date: "2026-05-09T08:27:00Z" },
          message: "Initial Commit",
          verification: { verified: false, reason: "unsigned" }
        },
        html_url: "https://github.com/stevencurtis/TestVersionControl/commit/7d33728b...",
        author: { login: "stevencurtis" },
        parents: []
      }
    ]
  },
  create_or_update_file: {
    operation: 'create_or_update_file',
    input: {
      owner: 'stevencurtis', repo: 'TestVersionControl',
      path: 'VersionControl/Helper.swift',
      content: 'aW1wb3J0IFN3aWZ0VUkKCnN0cnVjdCBIZWxwZXIge30K',
      message: 'Add helper utility',
      branch: 'reworkUI'
    },
    output: {
      content: {
        name: 'Helper.swift',
        path: 'VersionControl/Helper.swift',
        sha: 'b2d4f6a8c1e3b5d7f9a2c4e6b8d1f3a5c7e9b2d4',
        size: 247,
        url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/contents/VersionControl/Helper.swift?ref=reworkUI',
        html_url: 'https://github.com/stevencurtis/TestVersionControl/blob/reworkUI/VersionControl/Helper.swift',
        git_url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/blobs/b2d4f6a8...',
        download_url: 'https://raw.githubusercontent.com/stevencurtis/TestVersionControl/reworkUI/VersionControl/Helper.swift',
        type: 'file',
        _links: {}
      },
      commit: {
        sha: '8a1f2c4e9b3d7f6a2c8e4b1d9f3a7c5e8b2d6f1a',
        node_id: 'C_kwDOabc',
        url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/commits/8a1f2c4e...',
        html_url: 'https://github.com/stevencurtis/TestVersionControl/commit/8a1f2c4e...',
        author: { name: 'Steven Curtis', email: 's@curtis.dev', date: '2026-05-09T14:22:00Z' },
        committer: { name: 'Steven Curtis', email: 's@curtis.dev', date: '2026-05-09T14:22:00Z' },
        message: 'Add helper utility',
        tree: { sha: 't1r2e3e', url: '...' },
        parents: [{ sha: '3cdb4c6f8e2a1d7c9b3e5f8a2c4d6e8f1a3b5c7d', html_url: '...' }],
        verification: { verified: false, reason: 'unsigned' }
      }
    }
  },
  push_files: {
    operation: 'push_files',
    input: {
      owner: 'stevencurtis', repo: 'TestVersionControl', branch: 'reworkUI',
      files: [
        { path: 'VersionControl/Models/Product.swift', content: '...' },
        { path: 'VersionControl/Models/Cart.swift', content: '...' },
        { path: 'VersionControl/Models/User.swift', content: '...' }
      ],
      message: 'Add model layer'
    },
    output: {
      // Most MCP push_files implementations return the updated ref or the new commit object.
      // Showing the commit object here.
      sha: '9c1e3a5d7f9b1e3c5a7d9f1b3e5c7a9d1f3b5e7c',
      node_id: 'C_kwDOpush',
      url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/commits/9c1e3a5d...',
      html_url: 'https://github.com/stevencurtis/TestVersionControl/commit/9c1e3a5d...',
      author: { name: 'Steven Curtis', email: 's@curtis.dev', date: '2026-05-09T15:08:00Z' },
      committer: { name: 'Steven Curtis', email: 's@curtis.dev', date: '2026-05-09T15:08:00Z' },
      message: 'Add model layer',
      tree: { sha: 'tree123', url: '...' },
      parents: [{ sha: '8a1f2c4e9b3d7f6a2c8e4b1d9f3a7c5e8b2d6f1a' }],
      verification: { verified: false, reason: 'unsigned' }
    }
  },
  get_file_contents: {
    operation: 'get_file_contents',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', path: 'README.md' },
    output: {
      type: 'file',
      name: 'README.md',
      path: 'README.md',
      sha: '5e7c9a1d3f5b7e9c1a3d5f7b9e1c3a5d7f9b1e3c',
      size: 86,
      url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/contents/README.md',
      html_url: 'https://github.com/stevencurtis/TestVersionControl/blob/main/README.md',
      git_url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/blobs/5e7c9a1d...',
      download_url: 'https://raw.githubusercontent.com/stevencurtis/TestVersionControl/main/README.md',
      encoding: 'base64',
      content: 'IyBUZXN0VmVyc2lvbkNvbnRyb2wKClNhbXBsZSBpT1MgYXBwIGRlbW9uc3RyYXRpbmcgU3dpZnRVSSB2ZXJzaW9uIGNvbnRyb2wgd29ya2Zsb3cuCg=='
    }
  },
  create_branch: {
    operation: 'create_branch',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', branch: 'feat/observable', from_branch: 'main' },
    output: {
      ref: 'refs/heads/feat/observable',
      node_id: 'REF_kwDObranch',
      url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/refs/heads/feat/observable',
      object: {
        sha: '7d33728b1c0e9f3a5b7d9c1e3f5a7b9c1d3e5f7a',
        type: 'commit',
        url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/git/commits/7d33728b...'
      }
    }
  },
  create_repository: {
    operation: 'create_repository',
    input: { name: 'NewProject', description: 'Greenfield SwiftUI app', private: false, autoInit: true },
    output: {
      id: 928374651,
      node_id: 'R_kgDOnew',
      name: 'NewProject',
      full_name: 'stevencurtis/NewProject',
      owner: { login: 'stevencurtis', id: 4287, type: 'User' },
      description: 'Greenfield SwiftUI app',
      private: false,
      fork: false,
      visibility: 'public',
      default_branch: 'main',
      language: null,
      topics: [],
      stargazers_count: 0,
      watchers_count: 0,
      forks_count: 0,
      open_issues_count: 0,
      size: 0,
      archived: false,
      disabled: false,
      html_url: 'https://github.com/stevencurtis/NewProject',
      clone_url: 'https://github.com/stevencurtis/NewProject.git',
      ssh_url: 'git@github.com:stevencurtis/NewProject.git',
      created_at: '2026-05-09T15:30:00Z',
      updated_at: '2026-05-09T15:30:00Z',
      pushed_at: '2026-05-09T15:30:00Z'
    }
  },
  fork_repository: {
    operation: 'fork_repository',
    input: { owner: 'pointfreeco', repo: 'swift-composable-architecture' },
    output: {
      id: 928374652,
      name: 'swift-composable-architecture',
      full_name: 'stevencurtis/swift-composable-architecture',
      owner: { login: 'stevencurtis', id: 4287, type: 'User' },
      description: 'A library for building applications in a consistent and understandable way, with a focus on composition, testing, and ergonomics.',
      private: false,
      fork: true,
      visibility: 'public',
      default_branch: 'main',
      language: 'Swift',
      topics: ['swift', 'swiftui', 'composable', 'architecture', 'state-management'],
      license: { key: 'mit', spdx_id: 'MIT', name: 'MIT License' },
      homepage: 'https://www.pointfree.co',
      stargazers_count: 0,
      watchers_count: 0,
      forks_count: 0,
      open_issues_count: 0,
      size: 12483,
      archived: false,
      disabled: false,
      html_url: 'https://github.com/stevencurtis/swift-composable-architecture',
      clone_url: 'https://github.com/stevencurtis/swift-composable-architecture.git',
      created_at: '2026-05-09T15:35:00Z',
      updated_at: '2026-05-09T15:35:00Z',
      pushed_at: '2026-05-09T15:35:00Z'
    }
  },
  create_issue: {
    operation: 'create_issue',
    input: {
      owner: 'stevencurtis', repo: 'TestVersionControl',
      title: 'Memory leak in ProductStore when products array reloads',
      body: 'When products are reloaded via the refresh handler, the previous array isn\'t released.',
      labels: ['bug', 'priority:high']
    },
    output: {
      id: 2034812345,
      node_id: 'I_kwDOissue42',
      number: 42,
      title: "Memory leak in ProductStore when products array reloads",
      state: "open",
      state_reason: null,
      locked: false,
      user: { login: "ada-lovelace", id: 1, type: "User" },
      labels: [
        { id: 1, name: "bug", color: "d73a4a", description: "Something isn't working" },
        { id: 2, name: "priority:high", color: "ff6b35", description: "High priority" }
      ],
      assignees: [{ login: "stevencurtis", id: 4287 }],
      milestone: null,
      body: "When products are reloaded via the refresh handler, the previous array isn't released. After ~50 reloads memory usage doubles. Repro in MemoryTests.swift.",
      author_association: "CONTRIBUTOR",
      reactions: { total_count: 3, '+1': 2, heart: 1, '-1': 0, laugh: 0, hooray: 0, confused: 0, rocket: 0, eyes: 0 },
      comments: 0,
      created_at: "2026-05-09T15:40:00Z",
      updated_at: "2026-05-09T15:40:00Z",
      closed_at: null,
      closed_by: null,
      html_url: "https://github.com/stevencurtis/TestVersionControl/issues/42"
    }
  },
  get_issue: {
    operation: 'get_issue',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', issue_number: 42 },
    output: {
      id: 2034812345,
      number: 42,
      title: "Memory leak in ProductStore when products array reloads",
      state: "closed",
      state_reason: "completed",
      locked: false,
      user: { login: "ada-lovelace", id: 1, type: "User" },
      labels: [{ name: "bug", color: "d73a4a" }, { name: "fixed", color: "0e8a16" }],
      assignees: [{ login: "stevencurtis" }],
      milestone: { title: "v1.2.0", number: 4, due_on: "2026-06-01T00:00:00Z", state: "open" },
      body: "When products are reloaded via the refresh handler, the previous array isn't released. After ~50 reloads memory usage doubles.",
      author_association: "CONTRIBUTOR",
      reactions: { total_count: 7, '+1': 4, heart: 2, hooray: 1, '-1': 0, laugh: 0, confused: 0, rocket: 0, eyes: 0 },
      comments: 7,
      created_at: "2026-05-08T10:15:00Z",
      updated_at: "2026-05-09T16:02:00Z",
      closed_at: "2026-05-09T16:02:00Z",
      closed_by: { login: "stevencurtis", id: 4287 },
      html_url: "https://github.com/stevencurtis/TestVersionControl/issues/42"
    }
  },
  list_issues: {
    operation: 'list_issues',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', state: 'open', sort: 'updated' },
    output: [
      { id: 1, number: 42, title: "Memory leak in ProductStore when products array reloads", state: "open", user: { login: "ada-lovelace" }, comments: 4, html_url: "https://github.com/stevencurtis/TestVersionControl/issues/42" },
      { id: 2, number: 88, title: "Refactor ProductsView to use @Observable macro", state: "open", user: { login: "grace-hopper" }, comments: 12, pull_request: { url: "...", merged_at: null }, html_url: "https://github.com/stevencurtis/TestVersionControl/pull/88" },
      { id: 3, number: 38, title: "Add empty state to ProductsView", state: "open", user: { login: "grace-hopper" }, comments: 0 },
      { id: 4, number: 31, title: "Investigate scroll jank on iPad split-view", state: "open", user: { login: "stevencurtis" }, comments: 2 }
    ]
  },
  update_issue: {
    operation: 'update_issue',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', issue_number: 42, state: 'closed', labels: ['bug', 'fixed'] },
    output: {
      id: 2034812345,
      number: 42,
      title: "Memory leak in ProductStore when products array reloads",
      state: "closed",
      state_reason: "completed",
      user: { login: "ada-lovelace" },
      labels: [{ name: "bug", color: "d73a4a" }, { name: "fixed", color: "0e8a16" }],
      body: "When products are reloaded via the refresh handler, the previous array isn't released.",
      author_association: "CONTRIBUTOR",
      created_at: "2026-05-08T10:15:00Z",
      updated_at: "2026-05-09T16:05:00Z",
      closed_at: "2026-05-09T16:05:00Z",
      closed_by: { login: "stevencurtis" },
      comments: 7,
      html_url: "https://github.com/stevencurtis/TestVersionControl/issues/42"
    }
  },
  add_issue_comment: {
    operation: 'add_issue_comment',
    input: { owner: 'stevencurtis', repo: 'TestVersionControl', issue_number: 42, body: 'Fixed in #88. Releasing the array via weak self captures resolves it.' },
    output: {
      id: 1820394823,
      node_id: 'IC_kwDOcomment',
      url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/issues/comments/1820394823',
      html_url: 'https://github.com/stevencurtis/TestVersionControl/issues/42#issuecomment-1820394823',
      issue_url: 'https://api.github.com/repos/stevencurtis/TestVersionControl/issues/42',
      user: { login: 'stevencurtis', id: 4287, type: 'User' },
      body: 'Fixed in #88. Releasing the array via weak self captures resolves it.',
      author_association: 'OWNER',
      reactions: { total_count: 2, '+1': 2, '-1': 0, laugh: 0, hooray: 0, confused: 0, heart: 0, rocket: 0, eyes: 0 },
      created_at: '2026-05-09T16:02:00Z',
      updated_at: '2026-05-09T16:02:00Z'
    }
  },
  create_pull_request: {
    operation: 'create_pull_request',
    input: {
      owner: 'stevencurtis', repo: 'TestVersionControl',
      title: 'Refactor ProductsView to use @Observable macro',
      body: 'Migrates ProductStore from ObservableObject to the new @Observable macro.',
      head: 'feat/observable-products',
      base: 'main'
    },
    output: {
      id: 1928374651,
      number: 88,
      title: "Refactor ProductsView to use @Observable macro",
      state: "open",
      locked: false,
      user: { login: "grace-hopper", id: 2, type: "User" },
      head: { ref: "feat/observable-products", sha: "8a1f2c4e9b3d7f6a", repo: { full_name: "stevencurtis/TestVersionControl" } },
      base: { ref: "main", sha: "3cdb4c6f8e2a1d7c", repo: { full_name: "stevencurtis/TestVersionControl" } },
      body: "Migrates ProductStore from ObservableObject to the new @Observable macro. Eliminates 18 lines of boilerplate and removes the need for @Published.",
      labels: [{ name: "enhancement", color: "a2eeef" }],
      assignees: [{ login: "stevencurtis" }],
      requested_reviewers: [{ login: "ada-lovelace" }, { login: "mhamilton" }],
      requested_teams: [],
      milestone: null,
      author_association: "MEMBER",
      draft: false,
      merged: false,
      merged_at: null,
      merged_by: null,
      mergeable: true,
      mergeable_state: "clean",
      rebaseable: true,
      merge_commit_sha: null,
      commits: 4,
      additions: 42,
      deletions: 71,
      changed_files: 5,
      comments: 2,
      review_comments: 5,
      reactions: { total_count: 1, '+1': 1, '-1': 0, laugh: 0, hooray: 0, confused: 0, heart: 0, rocket: 0, eyes: 0 },
      created_at: "2026-05-09T11:30:00Z",
      updated_at: "2026-05-09T14:15:00Z",
      closed_at: null,
      html_url: "https://github.com/stevencurtis/TestVersionControl/pull/88"
    }
  },
  search_repositories: {
    operation: 'search_repositories',
    input: { query: 'swiftui language:swift stars:>5000', perPage: 10 },
    output: {
      total_count: 4127,
      incomplete_results: false,
      items: [
        { id: 234, full_name: "pointfreeco/swift-composable-architecture", stargazers_count: 12834, language: "Swift", score: 1.0, html_url: "https://github.com/pointfreeco/swift-composable-architecture" },
        { id: 235, full_name: "ivanvorobei/SwiftUI", stargazers_count: 8421, language: "Swift", score: 0.94 },
        { id: 236, full_name: "exyte/PopupView", stargazers_count: 3127, language: "Swift", score: 0.81 },
        { id: 237, full_name: "SwiftUIX/SwiftUIX", stargazers_count: 7294, language: "Swift", score: 0.78 }
      ]
    }
  },
  search_code: {
    operation: 'search_code',
    input: { q: '@Observable extension:swift' },
    output: {
      total_count: 1284,
      incomplete_results: false,
      items: [
        { name: 'ProductStore.swift', path: 'Sources/Store/ProductStore.swift', sha: 'abc', repository: { full_name: 'stevencurtis/TestVersionControl', id: 1 }, score: 1.0, html_url: 'https://github.com/...' },
        { name: 'CartStore.swift', path: 'Sources/Models/CartStore.swift', repository: { full_name: 'pointfreeco/swift-composable-architecture' }, score: 0.92 },
        { name: 'ContentView.swift', path: 'Examples/SwiftUI/ContentView.swift', repository: { full_name: 'ivanvorobei/SwiftUI' }, score: 0.78 }
      ]
    }
  },
  search_users: {
    operation: 'search_users',
    input: { q: 'swiftui location:london' },
    output: {
      // NOTE: search_users does NOT include followers/public_repos — those need a separate /users/{login} call
      total_count: 218,
      incomplete_results: false,
      items: [
        { id: 1, login: 'ada-lovelace', type: 'User', avatar_url: 'https://avatars.githubusercontent.com/u/1', html_url: 'https://github.com/ada-lovelace', score: 1.0, site_admin: false },
        { id: 2, login: 'grace-hopper', type: 'User', avatar_url: 'https://avatars.githubusercontent.com/u/2', html_url: 'https://github.com/grace-hopper', score: 0.91, site_admin: false },
        { id: 3, login: 'mhamilton', type: 'User', avatar_url: 'https://avatars.githubusercontent.com/u/3', html_url: 'https://github.com/mhamilton', score: 0.84, site_admin: false }
      ]
    }
  },
  search_issues: {
    operation: 'search_issues',
    input: { q: 'observable macro is:issue is:open' },
    output: {
      total_count: 87,
      incomplete_results: false,
      items: [
        { id: 1, number: 42, title: 'Memory leak in ProductStore', state: 'open', user: { login: 'ada-lovelace' }, comments: 4, score: 1.0 },
        { id: 2, number: 88, title: 'Refactor ProductsView to use @Observable macro', state: 'open', pull_request: { url: '...', merged_at: null }, user: { login: 'grace-hopper' }, comments: 12, score: 0.95 },
        { id: 3, number: 91, title: 'Crash when toggling observable property', state: 'open', user: { login: 'mhamilton' }, comments: 0, score: 0.81 }
      ]
    }
  },
  delete_repository: {
    operation: 'delete_repository',
    input: { owner: 'stevencurtis', repo: 'OldExperiment' },
    output: null  // 204 No Content
  }
};

const SAMPLE_ORDER = [
  'list_commits', 'get_issue', 'create_pull_request', 'add_issue_comment',
  'create_or_update_file', 'push_files', 'get_file_contents',
  'create_branch', 'create_repository', 'fork_repository',
  'create_issue', 'list_issues', 'update_issue',
  'search_repositories', 'search_code', 'search_users', 'search_issues',
  'delete_repository'
];

export default function App() {
  const [activeKey, setActiveKey] = useState('list_commits');
  const sample = SAMPLES[activeKey];
  return (
    <>
      <style>{demoStyles}</style>
      <div className="demo-stage">
        <div className="demo-bar">
          <span className="demo-label">demo · operation:</span>
          {SAMPLE_ORDER.map(k => (
            <button
              key={k}
              className={`demo-btn ${activeKey === k ? 'demo-btn-active' : ''}`}
              onClick={() => setActiveKey(k)}
            >
              {k}
            </button>
          ))}
        </div>
        <XcodeVCS operation={sample.operation} input={sample.input} output={sample.output} />
        <div className="demo-caption">
          <code>&lt;XcodeVCS operation=&#123;...&#125; input=&#123;...&#125; output=&#123;...&#125; /&gt;</code>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Styles
// ============================================================================
const styles = `
.xv-window {
  width: 100%;
  height: 100%;
  background: #f5f5f7;
  border: 1px solid #c8c8cc;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.04),
    0 30px 60px -20px rgba(0,0,0,0.55),
    0 16px 36px -10px rgba(0,0,0,0.35);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  font-size: 12px;
  color: #1d1d1f;
}

/* title bar */
.xv-window .xv-titlebar {
  height: 32px;
  background: linear-gradient(180deg, #ececec, #e2e2e2);
  border-bottom: 1px solid #c8c8cc;
  display: flex;
  align-items: center;
  padding: 0 12px;
  position: relative;
  flex-shrink: 0;
}
.xv-window .xv-traffic { display: flex; gap: 8px; }
.xv-window .tl { width: 12px; height: 12px; border-radius: 50%; border: 0.5px solid rgba(0,0,0,0.15); }
.xv-window .tl-r { background: #ff5f57; }
.xv-window .tl-y { background: #febc2e; }
.xv-window .tl-g { background: #28c840; }
.xv-window .xv-title-path {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #3c3c43;
  background: rgba(255,255,255,0.6);
  border: 1px solid #d0d0d4;
  padding: 2px 12px;
  border-radius: 5px;
  max-width: 620px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.xv-window .xv-title-path span {
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.xv-window .main { flex: 1; display: flex; min-height: 0; }

/* sidebar */
.xv-window .sidebar {
  width: 24%;
  background: #ebebee;
  border-right: 1px solid #d6d6d9;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 6px 0;
}
.xv-window .sidebar::-webkit-scrollbar { width: 7px; }
.xv-window .sidebar::-webkit-scrollbar-thumb { background: #c0c0c4; border-radius: 4px; }
.xv-window .sb-section { margin-bottom: 2px; }
.xv-window .sb-section-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px 4px 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #6c6c70;
  text-align: left;
}
.xv-window .sb-section-head:hover { color: #1d1d1f; }
.xv-window .sb-section-icon { color: #8e8e93; }
.xv-window .sb-rot { transform: rotate(-90deg); }
.xv-window .sb-count {
  margin-left: auto;
  font-size: 9.5px;
  color: #8e8e93;
  background: #d8d8dc;
  padding: 0 5px;
  border-radius: 7px;
  font-weight: 500;
}
.xv-window .sb-section-body { padding: 0 6px 4px; }
.xv-window .sb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 22px;
  border-radius: 4px;
  font-size: 11.5px;
  color: #1d1d1f;
}
.xv-window .sb-item-active { background: #d3d3d8; font-weight: 500; }
.xv-window .sb-empty {
  font-size: 11px;
  color: #a0a0a4;
  font-style: italic;
  padding: 2px 22px;
}

/* content */
.xv-window .content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;
}

/* commit graph */
.xv-window .cg-pane {
  flex: 0 0 230px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #d6d6d9;
  min-height: 0;
}
.xv-window .cg-head {
  display: flex;
  height: 24px;
  background: #f5f5f7;
  border-bottom: 1px solid #d6d6d9;
  font-size: 11px;
  color: #6c6c70;
  font-weight: 600;
  flex-shrink: 0;
}
.xv-window .cg-h { padding: 4px 10px; border-right: 1px solid #e1e1e4; }
.xv-window .cg-h-graph { width: 80px; }
.xv-window .cg-h-desc { flex: 1; }
.xv-window .cg-h-sha { width: 90px; }
.xv-window .cg-h-author { width: 130px; }
.xv-window .cg-h-date { width: 130px; border-right: none; }
.xv-window .cg-body { flex: 1; overflow-y: auto; }
.xv-window .cg-body::-webkit-scrollbar { width: 8px; }
.xv-window .cg-body::-webkit-scrollbar-thumb { background: #c8c8cc; border-radius: 4px; }
.xv-window .cg-row {
  display: flex;
  width: 100%;
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: 11.5px;
  cursor: pointer;
  text-align: left;
  color: #1d1d1f;
  border-bottom: 1px solid #f0f0f3;
}
.xv-window .cg-row:hover { background: #f5f5f7; }
.xv-window .cg-row-sel { background: #d8e8ff !important; }
.xv-window .cg-row-sel:hover { background: #d0e2ff !important; }
.xv-window .cg-c {
  padding: 6px 10px;
  border-right: 1px solid #f0f0f3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
}
.xv-window .cg-c-graph { width: 80px; position: relative; justify-content: center; min-height: 28px; }
.xv-window .cg-c-desc { flex: 1; }
.xv-window .cg-c-sha {
  width: 90px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  font-weight: 600;
}
.xv-window .cg-c-author { width: 130px; font-size: 11px; }
.xv-window .cg-c-date { width: 130px; border-right: none; font-size: 11px; font-weight: 500; }
.xv-window .cg-line {
  position: absolute;
  left: 50%;
  width: 2px;
  background: #4a8ce8;
  transform: translateX(-50%);
}
.xv-window .cg-line-up { top: 0; height: 50%; }
.xv-window .cg-line-down { bottom: 0; height: 50%; }
.xv-window .cg-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #4a8ce8;
  z-index: 1;
}
.xv-window .cg-dot-sel { background: #4a8ce8; }
.xv-window .cg-ref {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #e8f0fc;
  color: #1d4faf;
  border: 1px solid #b8d0ec;
  padding: 0 5px;
  border-radius: 3px;
  font-size: 10.5px;
  font-weight: 600;
  margin-right: 4px;
}
.xv-window .cg-ref-head { background: #fff4d6; color: #8a5a00; border-color: #e8c87a; }
.xv-window .cg-msg { overflow: hidden; text-overflow: ellipsis; }
.xv-window .cg-empty {
  padding: 24px;
  text-align: center;
  color: #8e8e93;
  font-style: italic;
}

/* request echo */
.xv-window .req-echo {
  padding: 8px 14px;
  background: #f8f8fa;
  border-bottom: 1px solid #e1e1e4;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: #3c3c43;
  white-space: nowrap;
  overflow-x: auto;
  flex-shrink: 0;
}
.xv-window .req-echo::-webkit-scrollbar { height: 4px; }
.xv-window .req-echo::-webkit-scrollbar-thumb { background: #c8c8cc; border-radius: 2px; }
.xv-window .echo-prompt { color: #8e8e93; font-weight: 700; }
.xv-window .echo-op { color: #1d4faf; font-weight: 600; }
.xv-window .echo-arg { margin-left: 8px; }
.xv-window .echo-k { color: #6c6c70; }
.xv-window .echo-v { color: #1d1d1f; }

/* detail */
.xv-window .detail {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.xv-window .detail-full { width: 100%; }
.xv-window .detail::-webkit-scrollbar { width: 8px; }
.xv-window .detail::-webkit-scrollbar-thumb { background: #c8c8cc; border-radius: 4px; }
.xv-window .dp-pad { padding: 16px 20px; flex: 1; }
.xv-window .dp-empty,
.xv-window .dp-empty-inline {
  padding: 30px;
  text-align: center;
  color: #8e8e93;
  font-style: italic;
}
.xv-window .dp-subject {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin-bottom: 6px;
  line-height: 1.35;
}
.xv-window .dp-body {
  font-size: 12px;
  color: #3c3c43;
  margin-bottom: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  border-left: 2px solid #d8e8ff;
  padding-left: 10px;
}
.xv-window .dp-meta {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 14px;
  margin-bottom: 14px;
  font-size: 11.5px;
}
.xv-window .dp-meta-row { display: contents; }
.xv-window .dp-meta-row > .dp-k {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6c6c70;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 10px;
}
.xv-window .dp-meta-row > .dp-v {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.xv-window .dp-mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
}
.xv-window .dp-trunc {
  display: flex;
}
.xv-window .dp-dim { color: #6c6c70; }
.xv-window .dp-pill {
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.xv-window .dp-pill-ok { background: #d4f4d4; color: #1f7a1f; }
.xv-window .dp-pill-no { background: #fcd4d4; color: #b21f1f; }
.xv-window .dp-pill-warn { background: #fff0c4; color: #8a5a00; }
.xv-window .dp-pill-info { background: #d4e4fc; color: #1d4faf; }
.xv-window .dp-pill-merged { background: #ead4fc; color: #6f42c1; }
.xv-window .dp-chip {
  display: inline-block;
  background: #f0f0f3;
  border: 1px solid #d6d6d9;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 10px;
  margin-right: 4px;
}
.xv-window .dp-chip-info {
  background: #e8f0fc;
  border-color: #b8d0ec;
  color: #1d4faf;
}
.xv-window .dp-link {
  color: #007aff;
  text-decoration: none;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.xv-window .dp-link:hover { text-decoration: underline; }
.xv-window .dp-reactions {
  display: inline-flex;
  gap: 6px;
  margin-left: 8px;
}
.xv-window .dp-rx {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #6c6c70;
  background: #f5f5f7;
  padding: 1px 5px;
  border-radius: 8px;
  border: 1px solid #e1e1e4;
}
.xv-window .dp-stats {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f5f5f7;
  border: 1px solid #e1e1e4;
  border-radius: 5px;
  margin-bottom: 14px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11.5px;
}
.xv-window .dp-stat-add { color: #1f7a1f; font-weight: 700; }
.xv-window .dp-stat-del { color: #b21f1f; font-weight: 700; }
.xv-window .dp-stat-total {
  color: #6c6c70;
  font-family: -apple-system, sans-serif;
  margin-left: auto;
}
.xv-window .dp-files { margin-top: 6px; }
.xv-window .dp-files-list { max-height: 360px; overflow-y: auto; }
.xv-window .dp-files-head {
  font-size: 10px;
  font-weight: 600;
  color: #6c6c70;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e1e1e4;
}
.xv-window .dp-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 11.5px;
}
.xv-window .dp-fstatus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  flex-shrink: 0;
}
.xv-window .st-A { background: #d4f4d4; color: #1f7a1f; }
.xv-window .st-M { background: #fff0c4; color: #8a5a00; }
.xv-window .st-D { background: #fcd4d4; color: #b21f1f; }
.xv-window .st-R { background: #d4e4fc; color: #1d4faf; }
.xv-window .st-C { background: #e4d4fc; color: #5a1faf; }
.xv-window .st-U { background: #e6e6e9; color: #6c6c70; }
.xv-window .dp-fname {
  flex: 1;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.xv-window .dp-fstat {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10px;
  white-space: nowrap;
}
.xv-window .fl-add { color: #1f7a1f; font-weight: 600; }
.xv-window .fl-del { color: #b21f1f; font-weight: 600; }
.xv-window .dp-state-open { color: #1f7a1f; }
.xv-window .dp-state-closed { color: #b21f1f; }
.xv-window .dp-hint {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff4d6;
  border: 1px solid #e8c87a;
  border-radius: 5px;
  font-size: 11px;
  color: #6a4400;
  line-height: 1.5;
}
.xv-window .dp-hint code {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10.5px;
  background: rgba(0,0,0,0.06);
  padding: 1px 4px;
  border-radius: 2px;
}
.xv-window .dp-issue-body { margin-top: 14px; }
.xv-window .dp-body-text {
  font-size: 12px;
  color: #3c3c43;
  line-height: 1.5;
  white-space: pre-wrap;
}
.xv-window .dp-raw {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10.5px;
  background: #f5f5f7;
  border: 1px solid #e1e1e4;
  border-radius: 5px;
  padding: 10px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
}
.xv-window .dp-raw-footer {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #e1e1e4;
}
.xv-window .dp-raw-toggle {
  background: transparent;
  border: 1px solid #d6d6d9;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10.5px;
  color: #6c6c70;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.xv-window .dp-raw-toggle:hover { background: #f0f0f3; color: #1d1d1f; }
.xv-window .dp-raw-footer .dp-raw { margin-top: 8px; }

/* status bar */
.xv-window .statusbar {
  height: 22px;
  background: #ebebee;
  border-top: 1px solid #d6d6d9;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 6px;
  font-size: 10.5px;
  color: #6c6c70;
  flex-shrink: 0;
}
.xv-window .sb-dot {
  width: 7px;
  height: 7px;
  background: #28c840;
  border-radius: 50%;
  display: inline-block;
}
.xv-window .sb-sep { color: #c0c0c4; }
.xv-window .sb-warn { color: #b21f1f; font-weight: 600; }
`;

const demoStyles = `
.demo-stage {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  gap: 14px;
  background: linear-gradient(180deg, #2a2a2c 0%, #1c1c1e 100%);
}
.demo-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1024px;
}
.demo-label {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: #a0a0a4;
}
.demo-btn {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  color: #e0e0e3;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 10.5px;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 80ms;
}
.demo-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.demo-btn-active {
  background: #007aff;
  border-color: #007aff;
  color: #fff;
}
.demo-caption {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: #a0a0a4;
}
.demo-caption code {
  background: rgba(255,255,255,0.08);
  padding: 1px 6px;
  border-radius: 3px;
  color: #e0e0e3;
}
`;
